import type { IsoDate, IsoInstant, UUID } from './domain';

/*
 * The asset module's shapes: the register, its reference data, counts and
 * consumable stock.
 *
 * These mirror the server's records in `com.schoolmanagement.asset.dto` field
 * for field. Where a field is optional here it is because the server may
 * legitimately omit it. Everything the server works out (warranty state,
 * below-reorder, count tallies, balances) arrives computed — nothing in this
 * module is recalculated in the browser.
 */

// -------------------------------------------------------------------- enums

export type AssetStatus = 'IN_STOCK' | 'IN_USE' | 'UNDER_MAINTENANCE' | 'LOST' | 'DISPOSED';

export type AssetCondition = 'NEW' | 'GOOD' | 'FAIR' | 'POOR' | 'DAMAGED';

export type AssetEventType =
  | 'REGISTERED'
  | 'ASSIGNED'
  | 'UNASSIGNED'
  | 'MOVED'
  | 'CONDITION_CHANGED'
  | 'MAINTENANCE_STARTED'
  | 'MAINTENANCE_COMPLETED'
  | 'REPORTED_LOST'
  | 'FOUND'
  | 'DISPOSED'
  | 'AUDITED';

/** What sort of place a location is — for filtering, not for rules. */
export type LocationKind =
  | 'CLASSROOM'
  | 'LABORATORY'
  | 'OFFICE'
  | 'STORE'
  | 'SPORTS'
  | 'LIBRARY'
  | 'OTHER';

/** Categories, locations and stock items are retired, never deleted. */
export type AssetRecordStatus = 'ACTIVE' | 'INACTIVE';

export type AssetAuditStatus = 'OPEN' | 'CLOSED';

export type StockMovementType = 'RECEIVE' | 'ISSUE' | 'ADJUST';

/** Computed on the server from today's date; EXPIRING means within 30 days. */
export type WarrantyState = 'NONE' | 'VALID' | 'EXPIRING' | 'EXPIRED';

export const ASSET_STATUSES: AssetStatus[] = [
  'IN_STOCK',
  'IN_USE',
  'UNDER_MAINTENANCE',
  'LOST',
  'DISPOSED',
];
export const ASSET_CONDITIONS: AssetCondition[] = ['NEW', 'GOOD', 'FAIR', 'POOR', 'DAMAGED'];
export const LOCATION_KINDS: LocationKind[] = [
  'CLASSROOM',
  'LABORATORY',
  'OFFICE',
  'STORE',
  'SPORTS',
  'LIBRARY',
  'OTHER',
];
export const STOCK_MOVEMENT_TYPES: StockMovementType[] = ['RECEIVE', 'ISSUE', 'ADJUST'];

// ----------------------------------------------------------------- register

export interface Asset {
  id: UUID;
  /** Issued by the school's counter at registration. */
  assetTag: string;
  name: string;
  categoryId: UUID;
  categoryName?: string;
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  vendor?: string;
  purchaseDate?: IsoDate;
  purchaseCost?: number;
  warrantyExpiresOn?: IsoDate;
  /** NONE, VALID, EXPIRING or EXPIRED — the server's verdict. */
  warrantyState: WarrantyState;
  locationId?: UUID;
  locationName?: string;
  custodianEmployeeId?: UUID;
  custodianEmployeeName?: string;
  custodianDepartmentId?: UUID;
  custodianDepartmentName?: string;
  condition: AssetCondition;
  status: AssetStatus;
  statusReason?: string;
  notes?: string;
  disposedAt?: IsoInstant;
  createdAt: IsoInstant;
  updatedAt: IsoInstant;
}

/**
 * Descriptive facts. Location and condition are honoured on registration
 * only — afterwards they change through the named operations, and the server
 * refuses them here.
 */
export interface AssetInput {
  name: string;
  categoryId: UUID;
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  vendor?: string;
  purchaseDate?: IsoDate;
  purchaseCost?: number;
  warrantyExpiresOn?: IsoDate;
  /** Registration only: where it starts. */
  locationId?: UUID;
  /** Registration only; defaults to NEW. */
  condition?: AssetCondition;
  notes?: string;
}

export interface AssetSearchParams {
  status?: AssetStatus;
  categoryId?: UUID;
  locationId?: UUID;
  employeeId?: UUID;
}

/** Exactly one of employeeId or departmentId — the server refuses both or neither. */
export interface AssignAssetInput {
  employeeId?: UUID;
  departmentId?: UUID;
  /** Optionally move it at the same time. */
  locationId?: UUID;
  notes?: string;
}

export interface MoveAssetInput {
  locationId: UUID;
  notes?: string;
}

export interface AssetConditionInput {
  condition: AssetCondition;
  notes?: string;
}

export interface CompleteMaintenanceInput {
  /** What the repair cost; recorded on the history, not the ledger. */
  cost?: number;
  condition: AssetCondition;
  notes?: string;
}

export interface FoundAssetInput {
  locationId: UUID;
  condition: AssetCondition;
  notes?: string;
}

/** One entry on an asset's history, newest first. */
export interface AssetEvent {
  id: UUID;
  eventType: AssetEventType;
  fromLocation?: string;
  toLocation?: string;
  fromCustodian?: string;
  toCustodian?: string;
  fromCondition?: AssetCondition;
  toCondition?: AssetCondition;
  cost?: number;
  notes?: string;
  recordedBy?: UUID;
  occurredAt: IsoInstant;
}

/** Counts the database states; nothing estimated. */
export interface AssetDashboard {
  /** Keyed by AssetStatus name. */
  assetsByStatus: Partial<Record<AssetStatus, number>>;
  warrantiesExpiringIn30Days: number;
  stockItemsAtOrBelowReorder: number;
  openAudits: number;
}

// ----------------------------------------------------------- reference data

export interface AssetCategory {
  id: UUID;
  code: string;
  name: string;
  description?: string;
  /** Consumables are counted as stock, not tagged individually. */
  consumable: boolean;
  status: AssetRecordStatus;
  /** How many assets and stock items use it. */
  usage: number;
}

export interface AssetCategoryInput {
  code: string;
  name: string;
  description?: string;
  /** Fixed once the category is in use — the server refuses a change. */
  consumable: boolean;
}

export interface AssetLocation {
  id: UUID;
  campusId: UUID;
  campusName?: string;
  name: string;
  kind: LocationKind;
  description?: string;
  status: AssetRecordStatus;
  assetCount: number;
}

export interface AssetLocationInput {
  campusId: UUID;
  name: string;
  kind: LocationKind;
  description?: string;
}

// ------------------------------------------------------------------- counts

export interface AssetAudit {
  id: UUID;
  auditNumber: string;
  /** Absent when the count covers the whole school. */
  locationId?: UUID;
  locationName?: string;
  status: AssetAuditStatus;
  notes?: string;
  expected: number;
  found: number;
  missing: number;
  unchecked: number;
  /** Found, but somewhere other than where the register said. */
  misplaced: number;
  startedAt: IsoInstant;
  closedAt?: IsoInstant;
}

export interface AssetAuditLine {
  assetId: UUID;
  assetTag: string;
  assetName: string;
  expectedLocation?: string;
  /** Absent until checked — an unchecked line is never presumed found. */
  found?: boolean;
  foundLocation?: string;
  foundCondition?: AssetCondition;
  notes?: string;
  checkedAt?: IsoInstant;
}

export interface StartAuditInput {
  /** Count one location; omit to count the whole school. */
  locationId?: UUID;
  notes?: string;
}

export interface AuditCheckInput {
  assetId: UUID;
  /** true = on the shelf; false = looked for and not there. */
  found: boolean;
  /** Where it actually was; defaults to the audit's location. */
  foundLocationId?: UUID;
  foundCondition?: AssetCondition;
  notes?: string;
}

/** What "mark missing as lost" returns. */
export interface MarkMissingLostResult {
  marked: number;
}

// -------------------------------------------------------------------- stock

export interface StockItem {
  id: UUID;
  code: string;
  name: string;
  categoryId: UUID;
  categoryName?: string;
  locationId?: UUID;
  locationName?: string;
  unit: string;
  quantityOnHand: number;
  reorderLevel: number;
  /** On hand at or below the reorder level — the server's verdict. */
  belowReorderLevel: boolean;
  status: AssetRecordStatus;
}

/** Never carries a quantity: stock changes only through movements. */
export interface StockItemInput {
  code: string;
  name: string;
  categoryId: UUID;
  locationId?: UUID;
  unit: string;
  reorderLevel: number;
}

export interface StockSearchParams {
  categoryId?: UUID;
  /** true lists items at or below their reorder level. */
  lowOnly?: boolean;
}

export interface StockMovementInput {
  movementType: StockMovementType;
  /** At least 0.01. */
  quantity: number;
  /** ADJUST only: true adds stock, false removes it. */
  increase?: boolean;
  /** ISSUE only: who took it. */
  issuedToEmployeeId?: UUID;
  reference?: string;
  /** Required for ADJUST — a stock-take correction needs its reason. */
  notes?: string;
}

export interface StockMovement {
  id: UUID;
  movementType: StockMovementType;
  quantity: number;
  increase?: boolean;
  balanceAfter: number;
  issuedToEmployeeId?: UUID;
  issuedToEmployeeName?: string;
  reference?: string;
  notes?: string;
  occurredAt: IsoInstant;
}

/**
 * The stock screen's pickers, readable with STOCK_VIEW alone. Categories are
 * the active consumable ones; locations are the active ones.
 */
export interface StockLookups {
  categories: { id: UUID; code: string; name: string }[];
  locations: { id: UUID; name: string; campusId?: UUID }[];
}
